/**
 * The rows of screen 02 «Сегодня» and screen 03 «День» (salon-mockup.html
 * tdFins / tdUm / tdCard / tdItem / tdDay, 2026-10-03; spec:
 * concepts/today-approved.html).
 *
 * Every visit is a card: start and «до» end, client, service, length · price.
 * Finished visits fold into «Завершено». The highlighted card carries
 * «Пришёл / Не пришёл»; unmarked visits group into one «Не отмечено» line.
 */
import { forwardRef, type MouseEvent, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { formatAmount } from '../../i18n';
import { serviceName } from '../../lib/serviceName';
import { dayTitle, hm, weekday } from '../../lib/time';
import type { Appointment, Gap, Locale } from '../../types';
import type { FinishedSummary, Highlight, Mark } from './model';

const minutesBetween = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / 60_000);

const Chev = () => (
  <span className="td-chev" aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m9 6 6 6-6 6" />
    </svg>
  </span>
);

/** «1 ч 30 мин» / «1 саат 30 мүн» — the short form the rows use. */
export function useShortDuration() {
  const { t } = useTranslation();
  return (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return [h ? t('today.durH', { count: h }) : '', m ? t('today.durM', { count: m }) : ''].filter(Boolean).join(' ');
  };
}

/**
 * «Вт, 6 окт» / «Шей, 6-окт» — the header badge for a day that is not today or
 * tomorrow. Names from the bundle, never Intl: WebKit (the iPhone app) prints
 * the short Russian month as «марта» where Chromium prints «мар» (seen
 * 2026-10-02 in the e2e run).
 */
export function dayTag(day: string, t: TFunction): string {
  const wd = (t('today.weekdayShort', { returnObjects: true }) as string[])[weekday(day)];
  const m = (t('today.monthShort', { returnObjects: true }) as string[])[Number(day.slice(5, 7)) - 1];
  return t('today.tag', { wd, d: Number(day.slice(8)), m });
}

/** The line under a day's title: «4 визита · 6 400 сом». */
export function useDaySum() {
  const { t, i18n } = useTranslation();
  return (count: number, sum: number) => (
    <>
      {t('today.visits', { count })} · <span className="td-mono">{formatAmount(String(sum), i18n.language as Locale)}</span>{' '}
      {t('money.som')}
    </>
  );
}

/** Today's header line after «Сегодня»: «4 700 / 13 000 сом», earned in bold green, «/ expected» in grey. */
export function IncomeLine({ earned, expected }: { earned: number; expected: number }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  return (
    <>
      {/* the slash and its spaces in the body font: a monospaced space is a digit wide (owner, 2026-10-04) */}
      <span className="earn">{formatAmount(String(earned), locale)}</span> /{' '}
      <span className="td-mono">{formatAmount(String(expected), locale)}</span> {t('money.som')}
    </>
  );
}

/** «Завершено 2 · 1 неявка», folded; opens in place to one row per finished visit. */
export const FinishedFold = forwardRef<
  HTMLDetailsElement,
  { finished: Appointment[]; summary: FinishedSummary; onOpen: (id: string) => void }
>(function FinishedFold({ finished, summary, onOpen }, ref) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  if (!finished.length) return null;
  return (
    <details ref={ref} className="td-fold">
      <summary>
        <Chev />
        <span>
          {t('today.finished', { count: summary.done })}
          {summary.noShows > 0 && <span className="ns"> · {t('today.noShows', { count: summary.noShows })}</span>}
        </span>
      </summary>
      {finished.map((a) => (
        <div
          key={a.id}
          className="td-fin"
          data-id={a.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(a.id)}
          onKeyDown={(e) => e.key === 'Enter' && onOpen(a.id)}
        >
          <span className="tm">{hm(a.starts_at)}</span>
          <span>
            <b>{a.client_name}</b> · {serviceName(a.service_name_ru, a.service_name_ky, locale)}
          </span>
          {a.status === 'no_show' ? (
            <span className="td-pill noshow">{t('today.noShow')}</span>
          ) : (
            <span className="pr">{formatAmount(a.price, locale)}</span>
          )}
        </div>
      ))}
    </details>
  );
});

/** «Пришёл» filled on the left, «Не пришёл» outlined on the right, two equal halves. */
function MarkButtons({ onMark }: { onMark: (m: Mark) => void }) {
  const { t } = useTranslation();
  const tap = (m: Mark) => (e: MouseEvent) => {
    e.stopPropagation();
    onMark(m);
  };
  return (
    <div className="td-mk">
      <button type="button" className="yes" onClick={tap('completed')}>
        {t('today.arrived')}
      </button>
      <button type="button" className="no" onClick={tap('no_show')}>
        {t('today.noShow')}
      </button>
    </div>
  );
}

/** «Не отмечено 2 · 2 700 сом не в доходе», always one line; opens to «Все пришли» and each visit's own buttons. */
export function UnmarkedLine({
  visits,
  onMark,
  onAll,
}: {
  visits: Appointment[];
  onMark: (appt: Appointment, m: Mark) => void;
  onAll: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  if (!visits.length) return null;
  const sum = visits.reduce((s, a) => s + Number(a.price), 0);
  return (
    <details className="td-um">
      <summary>
        <Chev />
        <span className="tx">
          {t('today.unmarked', { count: visits.length })}{' '}
          <span>· {t('today.notInIncome', { sum: formatAmount(String(sum), locale) })}</span>
        </span>
      </summary>
      <div className="td-um-body">
        <button type="button" className="td-um-all" onClick={onAll}>
          {t('today.allArrived')}
        </button>
        {visits.map((a) => (
          <div key={a.id} className="td-um-row" data-id={a.id}>
            <div className="r1">
              <span className="tm">
                {hm(a.starts_at)}–{hm(a.ends_at)}
              </span>
              <span className="nm">
                {a.client_name} <span className="sv">· {serviceName(a.service_name_ru, a.service_name_ky, locale)}</span>
              </span>
              <span className="pr">{formatAmount(a.price, locale)}</span>
            </div>
            <MarkButtons onMark={(m) => onMark(a, m)} />
          </div>
        ))}
      </div>
    </details>
  );
}

/**
 * One visit. `highlight` makes it the live card: «Скоро» / red «Опаздывает»
 * with the buttons, or «Сейчас» with time left and a bar once she came. A visit
 * marked «Пришёл» that is not the highlighted one says «Пришёл · до 13:30» —
 * on «Сегодня» only (`arrivedLine`): «День» draws plain cards (phone H).
 */
export const VisitCard = forwardRef<
  HTMLDivElement,
  {
    appt: Appointment;
    overlaps?: Appointment[];
    highlight?: Highlight;
    arrivedLine?: boolean;
    onOpen: () => void;
    onMark?: (m: Mark) => void;
  }
>(function VisitCard({ appt, overlaps = [], highlight, arrivedLine = false, onOpen, onMark }, ref) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const duration = useShortDuration();
  const live = highlight !== undefined;
  const late = highlight?.phase === 'late';
  const arrived = arrivedLine && appt.status === 'completed';
  return (
    <div
      ref={ref}
      className={`td-card${live ? ' live' : ''}${late ? ' late' : ''}`}
      data-id={appt.id}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && onOpen()}
    >
      {highlight && (
        <div className={`td-lv${late ? ' late' : ''}`}>
          <span>{t(`today.phase.${highlight.phase}`).toLocaleUpperCase()}</span>
          <span className="td-mono">{t(`today.phaseMin.${highlight.phase}`, { count: highlight.minutes })}</span>
        </div>
      )}
      <div className="top">
        <span className="st">{hm(appt.starts_at)}</span>
        <span className="en">{t('today.until', { time: hm(appt.ends_at) })}</span>
      </div>
      <div className="nm">{appt.client_name}</div>
      <div className="sv">{serviceName(appt.service_name_ru, appt.service_name_ky, locale)}</div>
      <div className="meta">
        {duration(minutesBetween(appt.starts_at, appt.ends_at))} · {formatAmount(appt.price, locale)} {t('money.som')}
      </div>
      {overlaps.length > 0 && (
        <div className="ovl">
          {t('today.overlapsWith', {
            who: overlaps.map((o) => `${o.client_name}, ${hm(o.starts_at)}–${hm(o.ends_at)}`).join('; '),
          })}
        </div>
      )}
      {arrived && !live && <div className="okl">{t('today.arrivedUntil', { time: hm(appt.ends_at) })}</div>}
      {highlight?.phase === 'now' && (
        <div className="td-bar" aria-hidden="true">
          <i style={{ width: `${highlight.pct}%` }} />
        </div>
      )}
      {live && highlight.phase !== 'now' && onMark && <MarkButtons onMark={onMark} />}
    </div>
  );
});

/** A break: only where a time_off row exists. */
export function BreakLine({ reason, starts, ends }: { reason: string | null; starts: string; ends: string }) {
  return (
    <div className="td-brk">
      {reason && <span>{reason}</span>}
      <span className="td-mono">
        {hm(starts)}–{hm(ends)}
      </span>
    </div>
  );
}

/** Free slot: grey start, «Свободно 45 мин», «до 16:30», a square «+». */
export function FreeLine({ gap, onBook }: { gap: Gap; onBook: () => void }) {
  const { t } = useTranslation();
  const duration = useShortDuration();
  const span = duration(gap.minutes);
  return (
    <div
      className="td-free"
      role="button"
      tabIndex={0}
      aria-label={`${t('today.free', { span })}, ${hm(gap.starts_at)}–${hm(gap.ends_at)}`}
      onClick={onBook}
      onKeyDown={(e) => e.key === 'Enter' && onBook()}
    >
      <span className="tm">{hm(gap.starts_at)}</span>
      <div>
        <div className="nm">{t('today.free', { span })}</div>
        <div className="sv">{t('today.until', { time: hm(gap.ends_at) })}</div>
      </div>
      <span className="add" aria-hidden="true">
        +
      </span>
    </div>
  );
}

/** Where a new day starts: «Завтра · Суббота, 3 октября» plus its line; a divider that scrolls away — the header names the day (option B). */
export const DayBand = forwardRef<HTMLElement, { day: string; tomorrow: boolean; line?: ReactNode }>(function DayBand(
  { day, tomorrow, line },
  ref,
) {
  const { t, i18n } = useTranslation();
  const title = dayTitle(day, i18n.language as Locale);
  return (
    <section ref={ref} className="td-day" data-day={day}>
      <div className="a">{tomorrow ? `${t('today.tomorrow')} · ${title}` : title}</div>
      {line && <div className="b">{line}</div>}
    </section>
  );
});

export function MoreRow({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="td-more" role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => e.key === 'Enter' && onOpen()}>
      <span className="a">{t('today.more')}</span>
      <span className="c" aria-hidden="true">
        ›
      </span>
    </div>
  );
}

/** «Гүлназ: Пришёл · Отменить», for 5 seconds after a mark. */
export function UndoBar({ label, onUndo }: { label: string | null; onUndo: () => void }) {
  const { t } = useTranslation();
  if (!label) return null;
  return (
    <div className="td-undo" role="status">
      <span>{label}</span>
      <button type="button" onClick={onUndo}>
        {t('today.undo')}
      </button>
    </div>
  );
}
